import '@shopify/polaris/build/esm/styles.css'
import './common/polaris-overwrites.css'
import 'react-quill/dist/quill.snow.css'

import { Banner, Frame, Page } from '@shopify/polaris'
import { I18nContext, I18nManager } from '@shopify/react-i18n'
import React, { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import { backendFetch } from './common/api'
import { ConnectPage } from './common/ConnectPage'
import { DevBar } from './common/DevBar'
import { getShopTimeZone } from './common/helpers'
import { MaintenanceBanner } from './common/MaintenanceBanner'
import {
  EligibleForReview,
  ShopifyReviewHandler,
  useEligibleForReviewSWR,
} from './common/ShopifyReviewHandler'
import {
  MaintenanceStatus,
  SystemContext,
  SystemData,
  UiProps,
  UiPropsContext,
} from './common/SystemData'
import { NativeNavigationMenu } from './common/Tabs'
import { getUiPropsFromData } from './common/ui-props'
import { AdminRoutePropagator, AppProvider, setupClarity, setupRollbar } from './main/helpers'
import { PageSwitch } from './main/PageSwitch'
import { loadIntercom, setupIntercom } from './vendor/intercom'

const anyWindow = window as any
const urlParams = new URLSearchParams(window.location.search)
const shopOrigin = urlParams.get('shop') || ''
const host = urlParams.get('host') || ''
const locale = urlParams.get('locale') || 'en-US'
const adminMode = urlParams.get('admin-mode')

setupRollbar(shopOrigin)

if (!adminMode) {
  setupClarity(shopOrigin)
}

// expose to the backend client
anyWindow.adminMode = adminMode
anyWindow.shopOrigin = shopOrigin
anyWindow.shopifyHost = host
anyWindow.newUserTest = urlParams.get('new_user_test') === '1'
anyWindow.eligibleForReview = { eligible: false, needsNpsSurvey: true } as EligibleForReview

const RootComponent = () => {
  const [systemData, setSystemData] = useState<SystemData | undefined>(anyWindow.systemData)
  const [uiProps, setUiProps] = useState<UiProps | undefined>(
    getUiPropsFromData(anyWindow.systemData)
  )
  const { data: eligibleForReviewRes } = useEligibleForReviewSWR()
  const shouldWait = !systemData
  const fullMaintenance = systemData?.app_maintenance_status === MaintenanceStatus.Full

  useEffect(() => {
    ;(async () => {
      let data = systemData

      if (!data) {
        data = (await backendFetch('GET', '/system-data')).data
        setSystemData(data)
        setUiProps(getUiPropsFromData(data))
      }

      if (!anyWindow.adminMode) {
        const isMobileApp = anyWindow.shopify?.environment?.mobile === true
        document.documentElement.classList.toggle('shopify-mobile-app', isMobileApp)

        loadIntercom(data?.intercomAppId)
        setupIntercom(data?.intercomAppId, data?.shopData)
      }
    })()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!eligibleForReviewRes) {
      return
    }

    anyWindow.eligibleForReview = eligibleForReviewRes
  }, [eligibleForReviewRes])

  const i18nManager = new I18nManager({
    locale,
    timezone: getShopTimeZone(systemData),
  })

  return (
    <BrowserRouter>
      <I18nContext.Provider value={i18nManager}>
        <AppProvider>
          <SystemContext.Provider value={systemData}>
            <UiPropsContext.Provider value={{ uiProps, setUiProps }}>
              {anyWindow.adminMode && <AdminRoutePropagator />}
              {!anyWindow.adminMode && <ShopifyReviewHandler />}
              {!anyWindow.adminMode && <NativeNavigationMenu />}
              <Frame
                logo={{
                  contextualSaveBarSource: '/images/contextual-save-bar-logo.svg',
                  width: 180,
                }}
              >
                {shouldWait && !fullMaintenance && (
                  <Page>
                    <Banner tone="warning">Please wait...</Banner>
                  </Page>
                )}
                {!shouldWait && !fullMaintenance && <PageSwitch />}
                {fullMaintenance && <MaintenanceBanner />}
                <DevBar />
              </Frame>
            </UiPropsContext.Provider>
          </SystemContext.Provider>
        </AppProvider>
      </I18nContext.Provider>
    </BrowserRouter>
  )
}

const rootEl = document.getElementById('root') as Element
const root = createRoot(rootEl)

root.render(
  <React.StrictMode>
    {shopOrigin && <RootComponent />}
    {!shopOrigin && <ConnectPage />}
  </React.StrictMode>
)
